import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Shield, ShieldOff, Trash2, Loader2, Mail, Phone, Users as UsersIcon, Briefcase, UserPlus, X, Activity } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UsersPage = () => {
  const { token, user: me } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [activity, setActivity] = useState([]);
  const [showActivity, setShowActivity] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', role: 'employee' });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(r.data);
    } catch { setUsers([]); }
  };
  const loadActivity = async () => {
    try {
      const r = await axios.get(`${API}/admin/activity`, { headers: { Authorization: `Bearer ${token}` } });
      setActivity(r.data);
    } catch { setActivity([]); }
  };

  useEffect(() => { if (token) load(); }, [token]);
  useEffect(() => { if (showActivity && token) loadActivity(); }, [showActivity, token]);

  const createEmployee = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/admin/users/create-employee`, form, { headers: { Authorization: `Bearer ${token}` } });
      setShowCreate(false);
      setForm({ name: '', email: '', phone: '', password: '', role: 'employee' });
      await load();
      toast({ title: 'Account created', description: `${form.email} can now sign in.` });
    } catch (e2) {
      toast({ title: 'Create failed', description: e2?.response?.data?.detail || 'Please try again.' });
    } finally { setCreating(false); }
  };

  const toggleAdmin = async (uid) => {
    try {
      await axios.post(`${API}/admin/users/${uid}/toggle-admin`, {}, { headers: { Authorization: `Bearer ${token}` } });
      load();
      toast({ title: 'Updated', description: 'Admin status changed.' });
    } catch (e) {
      toast({ title: 'Action failed', description: e?.response?.data?.detail || 'Please try again.' });
    }
  };

  const toggleEmployee = async (uid) => {
    try {
      await axios.post(`${API}/admin/users/${uid}/toggle-employee`, {}, { headers: { Authorization: `Bearer ${token}` } });
      load();
      toast({ title: 'Updated', description: 'Employee status changed.' });
    } catch (e) {
      toast({ title: 'Action failed', description: e?.response?.data?.detail || 'Please try again.' });
    }
  };

  const remove = async (uid) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/users/${uid}`, { headers: { Authorization: `Bearer ${token}` } });
      load();
      toast({ title: 'User deleted' });
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || 'Please try again.' });
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Users</span></div>
          <h1 className="font-serif-display text-4xl font-bold text-black">Manage staff & customers</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowActivity(true)} className="inline-flex items-center gap-2 border border-neutral-200 hover:border-black h-11 px-5 text-[12px] tracking-[0.15em] uppercase font-semibold transition-colors">
            <Activity className="w-4 h-4" /> Activity Log
          </button>
          <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 bg-[#c8102e] hover:bg-black text-white h-11 px-5 text-[12px] tracking-[0.15em] uppercase font-semibold transition-colors">
            <UserPlus className="w-4 h-4" /> Create Employee
          </button>
        </div>
      </div>

      {users === null ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#c8102e]" /></div>
      ) : users.length === 0 ? (
        <div className="bg-white p-12 border border-neutral-200 text-center">
          <UsersIcon className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
          <p className="text-neutral-500">No users yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 divide-y divide-neutral-100">
          {users.map((u) => {
            const isMe = u.id === me?.id;
            return (
              <div key={u.id} className="p-5 flex items-center gap-4">
                <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-serif-display font-bold ${u.is_admin ? 'bg-[#c8102e]' : 'bg-black'}`}>
                  {u.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-serif-display font-bold text-black">{u.name}</span>
                    {u.is_admin && <span className="text-[9px] tracking-[0.25em] uppercase bg-[#c8102e] text-white px-2 py-0.5 font-semibold">Admin</span>}
                    {u.is_employee && !u.is_admin && <span className="text-[9px] tracking-[0.25em] uppercase bg-black text-white px-2 py-0.5 font-semibold">Employee</span>}
                    {isMe && <span className="text-[9px] tracking-[0.25em] uppercase bg-neutral-100 text-neutral-600 px-2 py-0.5 font-semibold">You</span>}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-neutral-500 mt-1">
                    <a href={`mailto:${u.email}`} className="flex items-center gap-1.5 hover:text-[#c8102e]"><Mail className="w-3 h-3" /> {u.email}</a>
                    {u.phone && <a href={`tel:${u.phone}`} className="flex items-center gap-1.5 hover:text-[#c8102e]"><Phone className="w-3 h-3" /> {u.phone}</a>}
                    <span>Joined {new Date(u.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                {!isMe && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleEmployee(u.id)} disabled={u.is_admin} title={u.is_employee ? 'Remove employee' : 'Make employee'} className="w-9 h-9 flex items-center justify-center border border-neutral-200 hover:border-black transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                      <Briefcase className={`w-4 h-4 ${u.is_employee ? 'text-black' : 'text-neutral-400'}`} />
                    </button>
                    <button onClick={() => toggleAdmin(u.id)} title={u.is_admin ? 'Remove admin' : 'Make admin'} className="w-9 h-9 flex items-center justify-center border border-neutral-200 hover:border-black transition-colors">
                      {u.is_admin ? <ShieldOff className="w-4 h-4 text-black" /> : <Shield className="w-4 h-4 text-[#c8102e]" />}
                    </button>
                    <button onClick={() => remove(u.id)} title="Delete user" className="w-9 h-9 flex items-center justify-center border border-neutral-200 hover:border-[#c8102e] hover:bg-[#c8102e] hover:text-white transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="bg-black text-white p-5 flex items-center justify-between">
              <div className="flex items-center gap-3"><UserPlus className="w-5 h-5 text-[#c8102e]" /><h3 className="font-serif-display font-bold text-lg">Create Staff Account</h3></div>
              <button onClick={() => setShowCreate(false)}><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={createEmployee} className="p-6 space-y-4">
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-neutral-600 font-semibold mb-2">Full Name</label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12" />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-neutral-600 font-semibold mb-2">Email</label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12" />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-neutral-600 font-semibold mb-2">Phone (optional)</label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12" />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-neutral-600 font-semibold mb-2">Password (6+ chars)</label>
                <Input required minLength={6} type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-12" placeholder="Share this with them" />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-neutral-600 font-semibold mb-2">Role</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-12 border border-neutral-300 px-3 bg-white text-sm">
                  <option value="employee">Employee (view submissions, packages, reply)</option>
                  <option value="admin">Admin (full access, can edit anything)</option>
                </select>
              </div>
              <Button type="submit" disabled={creating} className="w-full bg-[#c8102e] hover:bg-black text-white h-12 font-semibold tracking-[0.15em] uppercase text-[13px]">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />} Create Account
              </Button>
            </form>
          </div>
        </div>
      )}

      {showActivity && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowActivity(false)}>
          <div className="bg-white max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-black text-white p-5 flex items-center justify-between">
              <div className="flex items-center gap-3"><Activity className="w-5 h-5 text-[#c8102e]" /><h3 className="font-serif-display font-bold text-lg">Staff Activity Log</h3></div>
              <button onClick={() => setShowActivity(false)}><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-2">
              {activity.length === 0 ? (
                <p className="text-sm text-neutral-500 text-center py-8">No activity yet.</p>
              ) : (
                activity.map((a) => (
                  <div key={a.id} className="p-3 border border-neutral-100 text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-black">{a.actor_name || 'Unknown'}</span>
                      <span className="text-[10px] text-neutral-400">{new Date(a.at).toLocaleString()}</span>
                    </div>
                    <div className="text-xs text-neutral-500"><span className="text-[#c8102e] font-semibold">{a.action}</span> {a.target ? '→ ' + a.target : ''} {a.note ? ' — ' + a.note : ''}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default UsersPage;
